import React, { useEffect, useMemo, useRef, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import multiMonthPlugin from '@fullcalendar/multimonth';
import interactionPlugin from '@fullcalendar/interaction'; // for selectable
import {
  endOfYear,
  format,
  getDate,
  isAfter,
  isBefore,
  isWithinInterval,
  startOfYear,
  subDays,
  subMonths,
} from 'date-fns';
import { getDateFromString } from '../../../utils/helpers';
import '../assets/styles/DateFilterComponent.scss';
import { filterOptions } from '../../../constants/filterOptions';

const DateFilterComponent = ({ onClose, onFilterUpdate, startValue, endValue, selectedFilter }) => {
  const currentDate = subDays(new Date(), 1);
  const [startDate, setStartDate] = useState(getDateFromString(startValue));
  const [endDate, setEndDate] = useState(getDateFromString(endValue));
  const calendarRef = useRef(null);
  const calendarWrapperRef = useRef(null);
  const startDateRef = useRef(getDateFromString(startValue));
  const endDateRef = useRef(getDateFromString(endValue));
  const [selectedDateFilter, setSelectedDateFilter] = useState(filterOptions[3]);
  const [colorizeCells, setColorizeCells] = useState(false); // [startDate, endDate
  let inBetweenCounter = 0;
  let isBeforeCounter = 0;

  function bodyOnClick(e) {
    if (calendarWrapperRef.current && !calendarWrapperRef.current.contains(e.target)) {
      onClose();
    }
  }

  useEffect(() => {
    const divsWithDataDate = document.querySelectorAll('div[data-date]');

    if (divsWithDataDate) {
      divsWithDataDate.forEach(function (divElement) {
        const dateAttribute = divElement.getAttribute('data-date');
        const divDate = new Date(dateAttribute);
        if (divDate > currentDate) {
          divElement.classList.add('hidden');
        }
      });
    }

    document.body.addEventListener('click', bodyOnClick);
    return () => {
      document.body.removeEventListener('click', bodyOnClick);
    };
  }, []);

  useEffect(() => {
    if (selectedFilter) {
      setSelectedDateFilter(selectedFilter);
      onSelectDateFilter(selectedFilter);
    }
  }, [selectedFilter]);

  useEffect(() => {
    const elements = document.querySelectorAll('td.fc-daygrid-day[data-date]');
    elements.forEach((element) => {
      const dateAttribute = element.getAttribute('data-date');
      const parsedDate = getDateFromString(dateAttribute);
      if (isWithinInterval(parsedDate, { start: startDate, end: endDate })) {
        element.style.backgroundColor = 'rgb(210, 227, 252)';
        let isStartDate = false;
        if (format(parsedDate, 'yyyy-MM-dd') === format(startDate, 'yyyy-MM-dd')) {
          element.classList.add('rounded-left');
          element.classList.remove('rounded-right');
          element.style.backgroundColor = 'white';
          isStartDate = true;
        } else {
          element.classList.remove('rounded-left');
        }
        if (format(parsedDate, 'yyyy-MM-dd') === format(endDate, 'yyyy-MM-dd')) {
          element.style.backgroundColor = 'white';
          element.classList.add('rounded-right');
        } else if (!isStartDate) {
          element.classList.remove('rounded-right');
        }
      } else {
        element.style.backgroundColor = 'white';
        element.classList.remove('rounded-left');
        element.classList.remove('rounded-right');
      }
      if (isAfter(parsedDate, currentDate)) {
        element.classList.add('disabled');
      }
    });
  }, [startDate, endDate, colorizeCells]);

  const onDateSelect = (info) => {
    const selectedDate = new Date(info.date.toISOString());
    const startDateDate = startDateRef.current;
    const endDateDate = endDateRef.current;

    if (isAfter(selectedDate, currentDate)) {
      return;
    }
    if (isBefore(selectedDate, startDateDate)) {
      isBeforeCounter += 1;
      if (isBeforeCounter % 2 === 0) {
        startDateRef.current = selectedDate;
      } else {
        startDateRef.current = selectedDate;
        endDateRef.current = startDateDate;
      }
    } else if (isAfter(selectedDate, endDateDate)) {
      startDateRef.current = endDateDate;
      endDateRef.current = selectedDate;
    } else if (isWithinInterval(selectedDate, { start: startDateDate, end: endDateDate })) {
      inBetweenCounter += 1;
      if (inBetweenCounter % 2 === 0) {
        endDateRef.current = selectedDate;
      } else {
        startDateRef.current = selectedDate;
      }
    }

    setStartDate(startDateRef.current);
    setEndDate(endDateRef.current);
    setSelectedDateFilter(filterOptions[3]);
  };

  const calendarMemo = useMemo(() => {
    return (
      <FullCalendar
        ref={calendarRef}
        plugins={[multiMonthPlugin, interactionPlugin]}
        initialView='multiMonthYear'
        validRange={{
          start: new Date(startOfYear(new Date('2023-01-01'))),
          end: new Date(endOfYear(currentDate)),
        }}
        visibleRange={{
          start: new Date(startOfYear(new Date('2023-01-01'))),
          end: new Date(endOfYear(currentDate)),
        }}
        contentHeight={330}
        multiMonthMaxColumns={1}
        multiMonthTitleFormat={{
          year: 'numeric',
          month: 'short',
        }}
        selectable={true}
        dateClick={onDateSelect}
        datesSet={() => {
          setColorizeCells((colorizeCells) => !colorizeCells);
        }}
        // dayCellDidMount={(cellInfo) => {
        //   const cellDate = cellInfo.date;
        //   // if (cellDate >= startDate && cellDate <= endDate) {
        //   //   cellInfo.el.classList.add('custom-background');
        //   // }
        //   if (isWithinInterval(cellDate, { start: startDate, end: endDate })) {
        //     cellInfo.el.classList.add('custom-background');
        //   }
        // }}
      />
    );
  }, []);

  const onSelectDateFilter = (option) => {
    setSelectedDateFilter(option);
    let startDateDate;
    let endDateDate = getDateFromString(format(currentDate, 'yyyy-MM-dd'));

    switch (option.value) {
      case 'last30Days':
        startDateDate = subDays(currentDate, 29);
        break;

      case 'last90Days':
        startDateDate = subDays(currentDate, 89);
        break;

      case 'last12Months':
        startDateDate = subMonths(currentDate, 12);
        break;

      default:
        startDateDate = startDateRef.current;
        endDateDate = endDateRef.current;
        break;
    }
    const sDate = getDateFromString(format(startDateDate, 'yyyy-MM-dd'));
    const eDate = getDateFromString(format(endDateDate, 'yyyy-MM-dd'));

    setStartDate(sDate);
    setEndDate(eDate);
    startDateRef.current = sDate;
    endDateRef.current = eDate;
  };

  const onApply = () => {
    onClose();
    onFilterUpdate(selectedDateFilter, startDate, endDate);
  };

  return (
    <div ref={calendarWrapperRef} className='date-filter-wrapper'>
      <div
        className='date-filter-container'
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        <div className='date-filter-container--left'>
          <div className='date-filter-container--left--list'>
            {filterOptions.map((item) => (
              <div
                key={item.value}
                onClick={() => onSelectDateFilter(item)}
                className={`date-filter-container--left--list--item${
                  selectedDateFilter.value === item.value ? ' active' : ''
                }`}
              >
                <span>{item.name}</span>
              </div>
            ))}
          </div>
        </div>
        <div className='date-filter-container--right'>
          <div className='calendar-container'>
            <span className='filter-option'>{selectedDateFilter.name}</span>
            <span className='title'>
              {format(startDate, 'MMM d, yyyy')} - {format(endDate, 'MMM d, yyyy')}
            </span>
            <div className='week-header'>
              <div>Sun</div>
              <div>Mon</div>
              <div>Tue</div>
              <div>Wed</div>
              <div>Thu</div>
              <div>Fri</div>
              <div>Sat</div>
            </div>
            <div className='calendar'>{calendarMemo}</div>
          </div>
        </div>
      </div>
      <div className='divider'></div>
      <div className='action-wrapper'>
        <button className='cancel' onClick={onClose}>
          Cancel
        </button>
        <button className='apply' onClick={onApply}>
          Apply
        </button>
      </div>
    </div>
  );
};

export default DateFilterComponent;
